import React from "react";
import { ArrowLeftRight, Wrench } from "lucide-react";
import useAccess from "../hooks/useAccess.jsx";
import SystemDiagnosticsSection from "./SystemDiagnosticsSection.jsx";
import WorkspacePageHeader from "./WorkspacePageHeader.jsx";
import LocalToolsBusinessCopyCard from "./LocalToolsBusinessCopyCard.jsx";
import ConfirmationModal from "./ConfirmationModal.jsx";
import { fetchLocalTools, previewCacheTransfer, fetchCacheTransfer, confirmCacheTransfer } from "../lib/localToolsApi.js";

const BUTTON = "inline-flex min-h-8 items-center justify-center gap-1.5 rounded-[4px] border border-accent bg-accent px-3 py-1 text-sm font-semibold text-white hover:border-accent-strong hover:bg-accent-strong focus-visible:ring-2 focus-visible:ring-accent/30 disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400 disabled:opacity-100 disabled:shadow-none";
const SECONDARY = "inline-flex min-h-8 items-center justify-center rounded-[4px] border border-slate-300 bg-white px-3 py-1 text-sm font-semibold text-slate-700 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900 disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400 disabled:opacity-100 disabled:shadow-none";
const LABELS = { to_local: "Railway → local", to_railway: "Local → Railway", both: "Dans les deux sens" };
const TABLE_LABELS = { geocode_cache: "Adresses", route_cache: "Trajets HERE" };
const number = (value) => new Intl.NumberFormat("fr-CA").format(value || 0);
const plannedCount = (job) => Object.values(job?.summary?.to_local || {}).reduce((sum, n) => sum + n, 0)
  + Object.values(job?.summary?.to_railway || {}).reduce((sum, n) => sum + n, 0);

function CacheSummary({ job }) {
  if (!job?.summary) return null;
  return <div className="overflow-x-auto rounded-[4px] border border-slate-300">
    <table className="w-full min-w-0 table-fixed border-collapse text-left text-xs" style={{ minWidth: 0 }}>
      <caption className="border-b border-slate-200 px-3 py-2 text-left font-semibold text-slate-900">{LABELS[job.direction]} — aperçu des ajouts</caption>
      <thead className="bg-slate-100 text-slate-700"><tr>
        <th className="p-2">Contenu</th><th className="p-2">Vers local</th><th className="p-2">Vers Railway</th>
      </tr></thead>
      <tbody>{Object.entries(TABLE_LABELS).map(([key, label]) => <tr key={key} className="border-t border-slate-200">
        <th className="p-2 font-medium">{label}</th><td className="p-2 tabular-nums">{number(job.summary.to_local[key])}</td>
        <td className="p-2 tabular-nums">{number(job.summary.to_railway[key])}</td>
      </tr>)}</tbody>
    </table>
  </div>;
}

function TransferResult({ job }) {
  if (!job?.result) return null;
  return <div className="grid gap-2 text-sm" aria-live="polite">
    {Object.entries(job.result).map(([side, tables]) => <div key={side} className="rounded-[4px] border border-slate-300 p-2">
      <p className="font-semibold text-slate-900">{LABELS[side]}</p>
      {Object.entries(tables).map(([key, counts]) => <p key={key} className="text-slate-600">{TABLE_LABELS[key]} : {number(counts.added)} ajouté(s), {number(counts.excluded)} non transférable(s), {number(counts.already_present)} déjà présent(s), dont {number(counts.conflicts)} conflit(s) conservé(s).</p>)}
    </div>)}
  </div>;
}

export default function LocalToolsPage() {
  const { access } = useAccess();
  const [availability, setAvailability] = React.useState(null);
  const [error, setError] = React.useState("");
  const [direction, setDirection] = React.useState("to_local");
  const [job, setJob] = React.useState(null);
  const [requesting, setRequesting] = React.useState(false);
  const [confirmation, setConfirmation] = React.useState(false);
  const pending = React.useRef(false);
  const storageKey = `grain.local-tools.cache-transfer.${access?.userId || "local"}`;
  const running = job?.status === "comparing" || job?.status === "transferring";

  React.useEffect(() => {
    let stopped = false;
    setJob(null);
    fetchLocalTools().then((value) => { if (!stopped) setAvailability(value); }).catch((failure) => { if (!stopped) setError(failure.message); });
    let previous;
    try { previous = sessionStorage.getItem(storageKey); } catch { /* Le suivi reste utilisable sans stockage navigateur. */ }
    if (previous) fetchCacheTransfer(previous).then((value) => { if (!stopped) { setJob(value); setDirection(value.direction); } }).catch(() => {
      try { sessionStorage.removeItem(storageKey); } catch { /* Aucun transfert ne dépend de ce stockage. */ }
    });
    return () => { stopped = true; };
  }, [storageKey]);

  React.useEffect(() => {
    if (!running || !job?.id) return undefined;
    let stopped = false;
    let timer;
    async function poll() {
      try {
        const next = await fetchCacheTransfer(job.id);
        if (!stopped) { setJob(next); setError(""); }
      } catch (failure) {
        if (!stopped && failure.status === 404) {
          setJob((current) => ({ ...current, status: "failed", error: failure.message }));
          try { sessionStorage.removeItem(storageKey); } catch { /* Un nouvel aperçu reste disponible. */ }
        } else if (!stopped) setError(`${failure.message} Le suivi réessaiera automatiquement.`);
      } finally {
        if (!stopped) timer = setTimeout(poll, 1000);
      }
    }
    timer = setTimeout(poll, 500);
    return () => { stopped = true; clearTimeout(timer); };
  }, [job?.id, running, storageKey]);

  async function compare() {
    if (pending.current || running) return;
    pending.current = true;
    setRequesting(true);
    setError("");
    setConfirmation(false);
    try {
      const next = await previewCacheTransfer(direction);
      setJob(next);
      try { sessionStorage.setItem(storageKey, next.id); } catch { /* L’aperçu appartient aussi à la session serveur. */ }
    } catch (failure) { setError(failure.message); }
    finally { pending.current = false; setRequesting(false); }
  }

  async function confirm() {
    if (pending.current || job?.status !== "preview") return;
    pending.current = true;
    setRequesting(true);
    setError("");
    try { setJob(await confirmCacheTransfer(job.id)); setConfirmation(false); }
    catch (failure) { setError(failure.message); }
    finally { pending.current = false; setRequesting(false); }
  }

  return <section className="mx-auto grid w-full min-w-0 gap-3 px-4 pb-5 pt-0">
    <WorkspacePageHeader icon={Wrench} title="Outils locaux" description="Pilotage Railway à distance et échanges de données, réservés au compte admin local." />
    <SystemDiagnosticsSection railwayOnly sectionIdPrefix="local-tools-section" />
    {error ? <p role="alert" className="rounded-[4px] border border-red-600 bg-white p-3 text-sm text-red-700">{error}</p> : null}
    {!availability ? !error && <p role="status" className="text-sm text-slate-600">Vérification de l’accès et des connexions…</p> : <div className="grid items-start gap-3 min-[1100px]:grid-cols-2">
      <LocalToolsBusinessCopyCard availability={availability.business_copy} />
      <article className="grid min-w-0 gap-3 rounded-[4px] border border-slate-300 bg-white p-4">
        <h2 className="flex items-center gap-2 text-base font-semibold text-slate-950"><ArrowLeftRight size={18} /> Cache des adresses et trajets</h2>
        <p className="text-sm leading-5 text-slate-600">Comparer les deux caches puis ajouter les résultats valides manquants. Chaque environnement conserve son fichier et ses entrées existantes.</p>
        <p className="text-xs leading-5 text-slate-600">Avant chaque transfert confirmé : contrôle d’intégrité, sauvegarde Railway et test de restauration. Une seule sauvegarde est conservée ; la précédente est remplacée après validation de la nouvelle.</p>
        <label className="grid gap-1 text-sm font-medium text-slate-700">Sens du transfert
          <select value={direction} disabled={running || requesting} onChange={(event) => {
            setDirection(event.target.value); setJob(null);
            try { sessionStorage.removeItem(storageKey); } catch { /* Aucun transfert sans confirmation serveur. */ }
          }} className="h-6 w-full rounded-[4px] border border-slate-300 bg-white px-2 py-0 text-sm focus-visible:ring-2 focus-visible:ring-accent/30 disabled:text-slate-400">
            {Object.entries(LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
        <button onClick={compare} disabled={!availability.cache_transfer?.available || running || requesting} className={`${BUTTON} w-fit`}>{job?.status === "comparing" ? "Comparaison en cours…" : "Comparer les caches"}</button>
        {!availability.cache_transfer?.available ? <p className="text-sm text-slate-600">{availability.cache_transfer?.message}</p> : null}
        {job?.status === "comparing" ? <p role="status" className="text-sm text-slate-600">Lecture des adresses et des trajets des deux environnements. Aucun contenu du cache n’est modifié.</p> : null}
        <CacheSummary job={job} />
        {job?.summary ? <div className="grid gap-1 text-xs text-slate-600">
          {[["local", "Local"], ["railway", "Railway"]].map(([side, label]) => <p key={side}>{label} : {number(job.summary[side].geocode_cache)} adresses et {number(job.summary[side].route_cache)} trajets en cache.</p>)}
          <p>Chaque entrée est vérifiée pendant le transfert : une entrée non valide n’est pas transférée et une entrée déjà présente n’est jamais remplacée. Le transfert ne fait aucun appel HERE.</p>
        </div> : null}
        {job?.status === "preview" ? plannedCount(job) > 0
          ? <button className={`${BUTTON} w-fit`} onClick={() => setConfirmation(true)}>Transférer les {number(plannedCount(job))} entrées proposées</button>
          : <p role="status" className="text-sm font-semibold text-slate-700">Aucune entrée manquante à transférer dans le sens choisi.</p> : null}
        {job?.status === "transferring" ? <div role="status" className="grid gap-1 text-sm text-slate-700">
          {job.progress.phase === "backup" ? <p>Contrôle d’intégrité, sauvegarde Railway et test de restauration en cours… Cette étape peut durer plusieurs minutes ; aucun transfert n’a encore démarré.</p>
            : job.progress.phase === "integrity_after" ? <p>Vérification finale de l’intégrité du cache Railway…</p>
              : <><p>Transfert en cours : {number(job.progress.done)} / {number(job.progress.total)} entrées traitées.</p>
                <progress className="w-full accent-accent" value={job.progress.done} max={Math.max(1, job.progress.total)} /></>}
        </div> : null}
        {job?.safety?.restoration_tested ? <div className="rounded-[4px] border border-slate-300 bg-white p-2 text-xs leading-5 text-slate-700">
          <p>Sauvegarde Railway créée et restauration testée. Une seule version est conservée.</p>
          <p>Intégrité avant transfert : vérifiée. Intégrité après transfert : {job.safety.integrity_after === true ? "vérifiée" : job.safety.integrity_after === false ? "échec" : "en attente"}.</p>
        </div> : null}
        {job?.status === "completed" ? <p role="status" className="rounded-[4px] border border-accent bg-white p-2 text-sm font-semibold text-accent-strong">Transfert terminé. Les ajouts ont été vérifiés dans le cache de destination.</p> : null}
        {job?.status === "failed" ? <p role="alert" className="rounded-[4px] border border-red-600 bg-white p-2 text-sm text-red-700">{job.error} {job.result ? "Les lots déjà ajoutés restent conservés ; leur bilan figure ci-dessous." : ""}</p> : null}
        <TransferResult job={job} />
        {job && !running ? <button className={`${SECONDARY} w-fit`} onClick={() => { setJob(null); try { sessionStorage.removeItem(storageKey); } catch { /* Facultatif. */ } }}>Fermer le bilan</button> : null}
      </article>
    </div>}
    {confirmation ? <ConfirmationModal title="Confirmer le transfert du cache" eyebrow={LABELS[job.direction]} tone="neutral"
      description="La sauvegarde Railway sera renouvelée et sa restauration testée avant les ajouts. Une seule version sera conservée. Le transfert sera refusé si une vérification ou l’espace disponible est insuffisant."
      confirmLabel={requesting ? "Démarrage…" : "Confirmer le transfert"} cancelLabel="Annuler" isBusy={requesting}
      onConfirm={confirm} onClose={() => !requesting && setConfirmation(false)} onCancel={() => !requesting && setConfirmation(false)} errorMessage={error}
      summaryItems={[{ label: "Sens", value: LABELS[job.direction] }, { label: "Ajouts proposés", value: number(plannedCount(job)) }]}
    ><CacheSummary job={job} /></ConfirmationModal> : null}
  </section>;
}
